/**
 * 本地导入使用的无 DOM 纯函数。
 * 这些函数也可在 Node 测试中运行，避免把文件选择器或 IndexedDB 行为伪装成单测。
 */

export type ImportFileLike = {
  name: string;
  size: number;
  type?: string;
  webkitRelativePath?: string;
};

export type StorageEstimateLike = {
  usage?: number;
  quota?: number;
};

const AUDIO_EXTENSIONS = /\.(mp3|flac|wav|ogg|m4a|aac|opus|webm)$/i;
// AMLL 官方解析器支持的歌词格式，加上项目原有的 SRT 降级格式。
const LYRIC_EXTENSIONS = /\.(lrc|ttml|srt|spl|yrc|qrc|lys|lyl|lqe)$/i;

export function isAudioImport(file: ImportFileLike): boolean {
  return Boolean(file.type?.startsWith('audio/')) || AUDIO_EXTENSIONS.test(file.name);
}

export function isLyricImport(file: ImportFileLike): boolean {
  return LYRIC_EXTENSIONS.test(file.name);
}

/** 浏览器文件夹选择器提供相对路径；普通文件选择保持文件名。 */
export function importRelativePath(file: ImportFileLike): string {
  return file.webkitRelativePath?.trim() || file.name;
}

/**
 * 用于重复导入判断的稳定键。
 * 路径存在时参与键，避免不同目录中同名同大小音频被误判为重复；
 * 普通文件选择器仍以文件名作为路径，因此再次选择同一文件会命中。
 */
export function importFingerprint(file: ImportFileLike, durationMs?: number): string {
  const durationSeconds = Math.round((durationMs || 0) / 1000);
  return `${importRelativePath(file)}\u0000${file.size}\u0000${durationSeconds}`;
}

export function isQuotaExceededError(error: unknown): boolean {
  if (!error) return false;
  const candidate = error as { name?: string; code?: number; message?: string };
  return candidate.name === 'QuotaExceededError'
    || candidate.code === 22
    || candidate.code === 1014
    || /quota|storage space|存储空间|配额/i.test(candidate.message || '');
}

/**
 * 在写 IndexedDB 前给出保守的配额判断。
 * quota/usage 缺失时返回 allowed=true，让浏览器实际写入决定结果。
 */
export function estimatePersistentImport(files: Array<Pick<ImportFileLike, 'size'>>, estimate?: StorageEstimateLike): {
  allowed: boolean;
  bytes: number;
  available?: number;
  reason?: 'quota-insufficient' | 'estimate-unavailable';
} {
  const bytes = files.reduce((total, file) => total + Math.max(0, Number(file.size) || 0), 0);
  const usage = Number(estimate?.usage);
  const quota = Number(estimate?.quota);
  if (!Number.isFinite(usage) || !Number.isFinite(quota) || quota <= 0) {
    return { allowed: true, bytes, reason: 'estimate-unavailable' };
  }
  const available = Math.max(0, quota - Math.max(0, usage));
  return available >= bytes
    ? { allowed: true, bytes, available }
    : { allowed: false, bytes, available, reason: 'quota-insufficient' };
}
